/**
 * Axios interceptors for MuseFlow API authentication.
 *
 * Request: attaches `Authorization: Bearer <token>` to every MuseFlow API
 * request via authService.getToken(). The header is set authoritatively here —
 * manual headers at call sites are overridden, so a stale or missing token can
 * never be sent (the "Bearer undefined" class of bug, MUS-264).
 *
 * Response: on 401, refreshes the token once (single-flight inside
 * AuthService) and retries the request. A 401 carrying invalid_grant means
 * the session is dead and is reported to AuthService, which tears down both
 * identity stacks and notifies the React layer.
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { authService } from './AuthService';
import logger from './Logger';

const apiOrigin = process.env.REACT_APP_BACKEND_URL ?? '';

function isMuseflowApiRequest(url: string | undefined): boolean {
  if (!url) return false;
  return (apiOrigin !== '' && url.startsWith(apiOrigin)) || url.startsWith('/api/');
}

interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Sets up axios interceptors for auth header injection and 401 handling.
 * Call this once during app initialization.
 */
export function setupAxiosInterceptors() {
  axios.interceptors.request.use(async (config) => {
    if (!isMuseflowApiRequest(config.url)) {
      return config;
    }
    const token = await authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Never send "Bearer undefined" — an absent header fails faster and
      // honestly at the API, and skips cargo-cult auth on public endpoints.
      delete config.headers.Authorization;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryableRequest | undefined;

      if (
        error.response?.status !== 401 ||
        !originalRequest ||
        !isMuseflowApiRequest(originalRequest.url)
      ) {
        return Promise.reject(error);
      }

      if (originalRequest._retry) {
        logger.warn('[Axios] Request already retried after refresh, giving up');
        return Promise.reject(error);
      }

      const responseData = error.response?.data as { error?: string } | undefined;
      if (responseData?.error === 'invalid_grant') {
        logger.error('[Axios] API returned 401 with invalid_grant — session is dead');
        await authService.reportSessionInvalid();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      logger.info('[Axios] 401 received, attempting token refresh');

      // Refresh is single-flight inside AuthService; concurrent 401s share it.
      // getToken() also handles the dead-session path (invalid_grant on
      // refresh), so a missing token here just means the retry cannot succeed.
      const freshToken = await authService.getToken();
      if (!freshToken) {
        logger.warn('[Axios] No token available after refresh attempt');
        return Promise.reject(error);
      }

      originalRequest.headers.Authorization = `Bearer ${freshToken}`;
      return axios(originalRequest);
    },
  );

  logger.info('[Axios] Auth interceptors configured');
}
